import { selectBasketCheckout, selectBasketIsEmpty, selectBasketSelectedPaymentKey, sendBasket } from 'containers/OrderDetails/basketSlice'
import { BlikPopup, CardPopup, RecurringConsent, getMethodDescription, getPaymentIcon, isWalletRedirect, useAvailableMethods } from 'payments'
import PaymentConfirmationPopup from 'payments/PaymentConfirmationPopup'
import usePaymentProcessing from './hooks/usePaymentProcessing'
import { useDispatch, useSelector } from 'react-redux'
import useOrderRedirect from 'hooks/useOrderRedirect'
import useBankTransfer from './hooks/useBankTransfer'
import { useState, useEffect, useRef } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import usePopupState from 'hooks/usePopupState'
import Skeleton from 'react-loading-skeleton'
import Loading from 'components/Loading'
import Option from 'components/Option'
import Error from 'components/Error'
import Copy from 'components/Copy'
import Summary from './Summary'
import { v7 } from 'uuid'
import 'payments/style.scss'

const Payment = ({ price, action, primaryTheme = true }) => {
  const { popup } = usePopupState()
  const [isLoading, setLoading] = useState(false)
  const checkout = useSelector(selectBasketCheckout)
  const isBasketEmpty = useSelector(selectBasketIsEmpty)
  const selectedPaymentKey = useSelector(selectBasketSelectedPaymentKey)
  const [paymentMethodsAreVisible, setPaymentMethodsAreVisible] = useState(selectedPaymentKey !== 'bank_transfer')
  const bankTransfer = useBankTransfer(setLoading, setPaymentMethodsAreVisible)
  const [warning, setWarning] = useState(false)
  const [selected, select] = useState({
    methodId: ''
  })

  const bottomRef = useRef(null)
  const paymentMethods = useAvailableMethods(checkout?.payments)
  const thereArePaymentMethods = paymentMethods && paymentMethods.length > 0

  useOrderRedirect()
  const dispatch = useDispatch()
  const { t } = useTranslation('payment')
  const { purchase, callback, generateBody, handleResult } = usePaymentProcessing(
    selected,
    isLoading,
    setLoading
  )

  const validateForm = () => {
    if (checkout && checkout?.to_pay?.now?.grossPrice === 0) {
      callback('none')
    } else if (selected.methodId) {
      if (warning) setWarning(false)
      if (action) action()
      else purchase()
    } else {
      setWarning(true)
    }
  }

  useEffect(() => {
    if (!isBasketEmpty && !checkout?.payments) {
      setLoading(true)
      dispatch(sendBasket({ step: 'checkout' })).then(() => {
        setLoading(false)
      })
    }
  }, [checkout?.payments, dispatch, isBasketEmpty])

  useEffect(() => {
    if (selected.methodId) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [selected.methodId])


  return (
    <>
      <div className={`payment --${primaryTheme ? 'primary' : 'secondary'}`}>
        <div className='title'>
          <div>{t('payment')}</div>
          <div>{t('chooseOneOfOptions')}</div>
        </div>

        {isBasketEmpty
          ? <Loading middleOfPage />
          : <div className='payment__main center'>
            {paymentMethodsAreVisible
              ? <div>
                <Summary price={price} />
                <p>{t('methodsAvailable')}</p>
                {warning && <p className='--warning'>{t('youMustChooseAPaymentMethod')}</p>}

                <div
                  className='payment__methods'
                  style={isLoading ? { opacity: 0.5 } : undefined}>
                  {thereArePaymentMethods
                    ? paymentMethods.map((payment) => {
                      const { id, name, isBlocked } = payment
                      const isSelected = selected.methodId === id
                      const icon = getPaymentIcon(payment)

                      const additionalContent = []
                      if (isSelected && payment.supportsRecurring) {
                        additionalContent.push(<RecurringConsent
                          key='recurring'
                          name={`recurring-${id}`}
                          checked={!!selected.saveForRecurring}
                          disabled={isLoading}
                          onGateway={payment.key === 'online'}
                          onChange={checked => select(prev => ({ ...prev, saveForRecurring: checked }))}
                        />)
                      }
                      if (isSelected && isWalletRedirect(payment)) {
                        additionalContent.push(<p
                          key='walletHint'
                          className='payment__hint'>
                          {t(payment.walletForm.wallet === 'gpay' ? 'chooseGooglePayOnGateway' : 'chooseApplePayOnGateway')}
                        </p>)
                      }

                      return <Option
                        key={id}
                        icon={icon}
                        title={name}
                        desc={getMethodDescription(payment)}
                        selected={isSelected}
                        additionalContent={additionalContent.length > 0 ? additionalContent : undefined}
                        action={() => {
                          if (payment.key === 'bank_transfer') {
                            setPaymentMethodsAreVisible(false)
                            if (!bankTransfer) {
                              const basketPayment = {
                                amount: checkout?.to_pay?.now?.grossPrice,
                                date: payment.date || 'now',
                                methodId: payment.id,
                                key: payment.key,
                              }
                              setLoading(true)
                              dispatch(sendBasket({ step: 'finalizeBankTransfer', payments: [basketPayment] })).then(res => {
                                setLoading(false)
                                if (!res.payload?.bankTransfer) {
                                  setPaymentMethodsAreVisible(true)
                                }
                              })
                            }

                          } else if (!isBlocked && !isLoading) {
                            // cała metoda idzie do stanu razem ze zgodą - zmiana metody czyści zgodę sama
                            select({ ...payment, methodId: id, saveForRecurring: false })
                          }
                        }} />
                    })
                    : <Error
                      staticPos
                      text={t('noPaymentMethods')}
                    />}
                </div>

                <ArrowBtn
                  enlarged
                  text={t('payNow')}
                  isLoading={isLoading}
                  disabled={!thereArePaymentMethods}
                  action={validateForm} />
                <BackBtn />
                <div ref={bottomRef} />
              </div>
              : <div>
                <Summary price={price} />
                <p>{t('paymentDetails')}</p>

                {isLoading
                  ? <div className='payment__transfer'>
                    <Skeleton className='skeleton__label' />
                    <Skeleton className='skeleton__value' />
                    <Skeleton className='skeleton__label' />
                    <Skeleton className='skeleton__value' />
                    <Skeleton className='skeleton__label' />
                    <Skeleton className='skeleton__value' />
                    <Skeleton className='skeleton__label' />
                    <Skeleton className='skeleton__value' />
                    <Skeleton className='skeleton__label' />
                    <Skeleton className='skeleton__value' />
                  </div>
                  : <div className='payment__transfer'>
                    <div className='payment__transfer__label'>{t('recipientName')}</div>
                    <div className='payment__transfer__value'>
                      <span>{bankTransfer?.receiverName}</span>
                      <Copy text={bankTransfer?.receiverName || ''} />
                    </div>
                    <div className='payment__transfer__label'>{t('accountNumber')}</div>
                    <div className='payment__transfer__value'>
                      <span>{bankTransfer?.bankAccNumber}</span>
                      <Copy text={bankTransfer?.bankAccNumber || ''} />
                    </div>
                    <div className='payment__transfer__label'>{t('transferTitle')}</div>
                    <div className='payment__transfer__value'>
                      <span>{bankTransfer?.paymentTitle}</span>
                      <Copy text={bankTransfer?.paymentTitle || ''} />
                    </div>
                    <div className='payment__transfer__label'>{t('dueDate')}</div>
                    <div className='payment__transfer__value'>{bankTransfer?.paymentDeadline}</div>
                    <div className='payment__transfer__label'>{t('invoice')}</div>
                    {bankTransfer?.status === 'INVOICE_PENDING'
                      ? <Skeleton className='skeleton__value' />
                      : bankTransfer?.invoices
                        ? <div className='payment__transfer__value'>
                          {bankTransfer.invoices.map((invoice, idx) => invoice.pdf
                            ? <span
                              key={idx}
                              className='--link'
                              onClick={() => {
                                window.open(invoice.pdf, '_blank')
                              }}>
                              {invoice?.number || `${t('invoice')} ${idx + 1}`}
                            </span>
                            : <span key={idx}>{invoice?.number || `${t('invoice')} ${idx + 1}`}</span>)}
                        </div>
                        : null}
                    <div className='payment__transfer__label'>{bankTransfer?.message}</div>
                  </div>}

                <ArrowBtn
                  enlarged
                  text={t('finish')}
                  isLoading={isLoading}
                  action={() => {
                    const bankTransferPayment = checkout.payments.find(payment => payment.key === 'bank_transfer')
                    const payment = bankTransferPayment
                      ? {
                        amount: checkout?.to_pay?.now?.grossPrice,
                        date: bankTransferPayment.date || 'now',
                        methodId: bankTransferPayment.id,
                        key: bankTransferPayment.key
                      }
                      : undefined
                    setLoading(true)
                    callback(payment)
                  }} />
                <BackBtn
                  text={t('payOnline')}
                  action={() => {
                    setPaymentMethodsAreVisible(true)
                  }}
                />
              </div>}
          </div>}
      </div>

      {popup && <div>
        {popup.type === 'blik'
          ? (() => {
            let localPaymentId = ''
            return <BlikPopup
              onResult={result => {
                handleResult(result, localPaymentId, popup.payment)
              }}
              getBody={extra => {
                localPaymentId = v7()
                return generateBody(localPaymentId, extra)
              }}
            />
          })()
          : popup.type === 'card'
            ? (() => {
              let localPaymentId = ''
              return <CardPopup
                cardForm={popup.cardForm || selected.cardForm}
                saveForRecurring={popup.saveForRecurring}
                onResult={result => {
                  handleResult(result, localPaymentId, popup.payment)
                }}
                getBody={extra => {
                  localPaymentId = v7()
                  return generateBody(localPaymentId, extra)
                }}
              />
            })()
            : popup.type === 'paymentConfirmation'
              ? <PaymentConfirmationPopup
                isLoading={isLoading}
                callback={() => {
                  const bankTransferPayment = checkout.payments.find(payment => payment.key === 'bank_transfer')
                  const payment = bankTransferPayment
                    ? {
                      amount: checkout?.to_pay?.now?.grossPrice,
                      date: bankTransferPayment.date || 'now',
                      methodId: bankTransferPayment.id,
                      key: bankTransferPayment.key
                    }
                    : undefined
                  setLoading(true)
                  callback(payment)
                }}
              />
              : null}
      </div>}
    </>
  )
}

export default Payment
